import { trpc } from "../trpc";
import {
  FolderTreeItem,
  insertFolderIntoTree,
  replaceFolderInTree,
} from "../folderTree";
import type { FolderTreeOutput } from "@zapier/bff";
import { useCurrentAccountId } from "@zapier/identity";
import { TEMP_FOLDER_ID } from "@zapier/utils";

export function useCreateFolder() {
  const utils = trpc.useUtils();
  const accountId = useCurrentAccountId();

  const mutation = trpc.folder.create.useMutation({
    // Callback to handle optimistic updates in RQ cache
    onMutate: ({ name, isShared }) => {
      utils.folder.folderTree.cancel(); // cancel outgoing refetches
      // Update Folder under folderTree key
      utils.folder.folderTree.setData(
        {},
        (old: FolderTreeOutput | undefined) => {
          if (old?.hash) {
            const newFolder = {
              name,
              isShared,
              accountId: Number(accountId) || 0,
              id: TEMP_FOLDER_ID,
              children: [],
              ownerId: undefined,
              parentId: undefined,
              rootId: undefined,
            };

            const updatedFolderTree = insertFolderIntoTree(old.tree, newFolder);

            // this needs to return a new object ref for RQ to detect the change
            return {
              ...old,
              // update hash
              hash: {
                ...old.hash,
                [TEMP_FOLDER_ID]: newFolder,
              },
              // update tree
              tree: updatedFolderTree as FolderTreeItem[],
            };
          }

          return old;
        },
      );
    },
    // Replace the temporary optimistic folder with the real server data
    // so the cache has the real folder ID before component-level onSuccess
    // callbacks run (e.g. navigation to the new folder URL).
    onSuccess: (data) => {
      utils.folder.folderTree.setData(
        {},
        (old: FolderTreeOutput | undefined) => {
          if (!old?.hash) {
            return old;
          }

          const realFolder: FolderTreeItem = {
            id: data.id,
            name: data.name,
            isShared: data.isShared,
            accountId: data.accountId,
            ownerId: data.ownerId,
            parentId: data.parentId,
            rootId: data.rootId,
            children: [],
          };

          const updatedTree = replaceFolderInTree(
            old.tree,
            TEMP_FOLDER_ID,
            realFolder,
          );

          // Remove the temp entry from hash and add the real one
          const { [TEMP_FOLDER_ID]: _removed, ...remainingHash } = old.hash;

          return {
            ...old,
            tree: updatedTree as FolderTreeItem[],
            hash: {
              ...remainingHash,
              [data.id]: realFolder,
            },
          };
        },
      );
    },
    // Callback to handle cache invalidation after mutation is completed
    onSettled: () => {
      // Invalidate folderTree key to refetch data
      utils.folder.folderTree.invalidate();
    },
  });

  return {
    create: mutation.mutate,
    isLoading: mutation.isPending,
  };
}
